<!--
  StoreCategories/index.vue -- C1 (shell) + C2 (card UI + persistence) +
  C4 (delete dialog + empty/add polish).

  Card: Store Editor: Categories & Storefront page (ADR 0059)
  https://trello.com/c/a7j1VV6D
  Plan: docs/planning/2026-07-23-store-categories-implementation-plan.md
        (Phase C, C1 + C2 + C4)
  Design: planning/design-handoffs/handoff_2026-07-23/
          2026-07-23-store-categories/ (README.md, design/cats-store.jsx,
          design/Locked Decisions.md, adr/0059)

  C2 SCOPE: stacked, fully-expanded CategoryCard-per-category (Home Page
  pinned first), an Add-category bar, and real persistence to B1
  (api/src/endpoints/store_categories.py) for every editable field:
  rename (name -> PATCH, server re-derives slug), rich_text (debounced
  PATCH), visibility (immediate PATCH), and banner (POST /upload_image
  then PATCH banner_image). NOT this slice: drag reordering (C3) and the
  delete-confirmation dialog + polished add/empty states (C4).

  C4 SCOPE: wires the delete button (rendered since C2, no-op until now)
  to a DestructiveConfirmDialog (reused read-only from
  StoreProducts/Editor/DestructiveConfirmDialog.vue) that previews the
  category's ALREADY-LOADED products -- first 5 + "and NN more" -- so the
  distributor sees exactly what will be un-assigned BEFORE confirming.
  Confirming calls DELETE /api/stores/<id>/categories/<cid> (B1); the
  response's own affected-product list is post-hoc confirmation only and
  is NOT what builds the dialog's preview. C4 also polishes the ADR-0022
  empty state (only Home Page exists -> concrete "Add your first
  category" CTA, not a bare message) and focuses the name field on a
  freshly-added unsaved card (CategoryCard.vue's mounted() hook).

  Persistence model: each category is its OWN REST resource (unlike
  campaign fields, which mutate the campaign object in place and ride
  the parent's 350ms autosave debounce -- see StoreAddEdit.vue
  AUTOSAVE_DEBOUNCE_MS). This section calls B1 directly, mirroring
  StoreEmailTemplates.vue's per-store email_customizations pattern:
  text-like fields (rich_text) debounce locally before PATCHing;
  discrete toggles (hidden) and uploads PATCH immediately. @campaign-
  changed is still emitted on every persisted edit so the parent's
  "unsaved changes" affordance stays accurate, even though this
  section's own PATCH already round-trips the data (matches the
  "PATCH directly on field blur/change" option in the dispatch brief).

  Data: GET /api/stores/<id>/categories (B1, merged) -- returns an array
  of category rows (home first, then nav_order), each carrying its
  assigned products in sort_order + a hidden-product count. See
  api/src/endpoints/store_categories.py::_serialize_category_with_products.

  CC1 SCOPE (ADR 0059 2026-07-27 revision, Jake exec product-design call --
  post-launch UX polish): cards render collapsed by default in a
  SINGLE-OPEN accordion. `openCategoryId` here is the one source of truth
  for which card (if any) is expanded -- component-managed UI state, never
  persisted or sent to B1. "Add category" moved from a bottom bar to the
  TOP of the page; clicking it still creates the draft row exactly as
  before, but now ALSO opens it (collapsing whatever else was open) and
  scrolls it into view. The category-drag Sortable instance also gained
  `forceFallback: true` to fix a nested drop-target-highlight bug Jake saw
  live on 8087 (see _initCategorySortable's comment for the root cause).

  Test-id prefix: ct-categories-*
-->
<template>
  <div class="store-categories" data-testid="ct-categories">
    <div v-if="loading" class="store-categories__loading" data-testid="ct-categories-loading">
      Loading categories...
    </div>

    <div v-else-if="hasFetchError" class="store-categories__error" data-testid="ct-categories-error">
      <span class="tf-icon store-categories__error-icon">error_outline</span>
      <h3 class="store-categories__error-title">Couldn't load categories -- try again</h3>
      <p class="store-categories__error-body">
        We weren't able to fetch this store's categories. Retry, or check
        back shortly.
      </p>
      <button
        type="button"
        class="store-categories__retry"
        data-testid="ct-categories-error-retry"
        @click="fetchCategories"
      >
        <span class="tf-icon">refresh</span>
        Retry
      </button>
    </div>

    <div v-else-if="categories.length === 0" class="store-categories__empty" data-testid="ct-categories-empty">
      <span class="tf-icon store-categories__empty-icon">category</span>
      <h3 class="store-categories__empty-title">Only the Home Page so far</h3>
      <p class="store-categories__empty-body">
        Add your first category to start shaping the storefront nav.
      </p>
    </div>

    <template v-else>
      <div class="store-categories__page-head">
        <div class="store-categories__page-head-copy">
          <h2 class="store-categories__page-title">Categories &amp; Storefront</h2>
          <p class="store-categories__page-lede">
            Group the products in this store, give each group its own banner
            and description, and arrange how shoppers browse. The Home Page
            category leads the storefront. Hide a category to keep it out of
            the storefront nav and share it by link only.
          </p>
        </div>
        <span class="store-categories__count" data-testid="ct-categories-header-count">
          {{ categoryCountLabel }}
        </span>
      </div>

      <!-- CC1 (ADR 0059 2026-07-27 revision): Add category moves to the
           TOP of the page, above the list, so it is reachable without
           scrolling past every existing card first. -->
      <button
        type="button"
        class="store-categories__add-bar"
        data-testid="ct-categories-add-bar"
        :disabled="!editMode"
        @click="onAddCategory"
      >
        <span class="tf-icon">add</span>
        Add category
      </button>

      <div class="store-categories__list" data-testid="ct-categories-list">
        <!-- Home Page is pinned first and lives OUTSIDE the draggable
             container so sortablejs can never move it (Locked
             Decisions.md: pin, not drag handle; always first). It is
             still expandable/collapsible like any other card (CC1). -->
        <category-card
          v-for="category in homeCategories"
          :key="category.id"
          :ref="cardRef(category.id)"
          :category="category"
          :store-slug="storeSlug"
          :store-base-url="storeBaseUrl"
          :is-new="unsavedCategoryIds.has(category.id)"
          :edit-mode="editMode"
          :banner-uploading="uploadingBannerFor === category.id"
          :expanded="openCategoryId === category.id"
          @update-name="onUpdateName"
          @update-rich-text="onUpdateRichText"
          @update-hidden="onUpdateHidden"
          @update-banner="onUpdateBanner"
          @update-product-order="onUpdateProductOrder"
          @delete-category="onDeleteCategory"
          @toggle-expand="onToggleExpand"
        />

        <!-- Non-home categories are drag-reorderable (nav_order). The
             ref is the sortablejs container; each card carries its
             category id so onEnd's old/new index maps back to a row. -->
        <div
          ref="categoryList"
          class="store-categories__reorderable"
          data-testid="ct-categories-reorderable"
        >
          <category-card
            v-for="category in nonHomeCategories"
            :key="category.id"
            :ref="cardRef(category.id)"
            :category="category"
            :store-slug="storeSlug"
            :store-base-url="storeBaseUrl"
            :is-new="unsavedCategoryIds.has(category.id)"
            :edit-mode="editMode"
            :banner-uploading="uploadingBannerFor === category.id"
            :expanded="openCategoryId === category.id"
            :data-category-id="category.id"
            @update-name="onUpdateName"
            @update-rich-text="onUpdateRichText"
            @update-hidden="onUpdateHidden"
            @update-banner="onUpdateBanner"
            @update-product-order="onUpdateProductOrder"
            @delete-category="onDeleteCategory"
            @toggle-expand="onToggleExpand"
          />
        </div>

        <!-- ADR 0022 empty-state convention: a brand-new store (only
             Home Page exists) gets a concrete, actionable CTA -- never a
             bare "no data" message. Shown alongside the add-bar above
             (which stays for returning users who already know the
             gesture); this panel is the first-run nudge. -->
        <div
          v-if="nonHomeCategories.length === 0"
          class="store-categories__empty-state"
          data-testid="ct-categories-empty-state"
        >
          <h3 class="store-categories__empty-state-title">Add your first category</h3>
          <p class="store-categories__empty-state-body">
            Categories let shoppers browse this store by group -- like Polos,
            Outerwear, or Headwear -- each with its own banner, description,
            and storefront link.
          </p>
          <button
            type="button"
            class="store-categories__empty-state-cta"
            data-testid="ct-categories-empty-cta"
            :disabled="!editMode"
            @click="onAddCategory"
          >
            <span class="tf-icon">add</span>
            Add category
          </button>
        </div>
      </div>
    </template>

    <!-- Delete confirmation (C4). Reused read-only from the product
         editor -- see StoreProducts/Editor/DestructiveConfirmDialog.vue.
         The affected-product preview below is built from
         deleteTargetCategory (the ALREADY-LOADED category row from the
         page's own `categories` state), never from the DELETE response --
         the user must see what will be un-assigned BEFORE confirming. -->
    <destructive-confirm-dialog
      v-if="deleteTargetCategory"
      v-model="deleteDialogOpen"
      data-testid="ct-categories-delete-dialog"
      title="Delete this category?"
      :description="deleteDialogDescription"
      :consequences="deleteDialogConsequences"
      confirm-label="Delete category"
      confirm-color="error"
      @confirm="onConfirmDeleteCategory"
      @cancel="onCancelDeleteCategory"
    />
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { Sortable } from "sortablejs";
import { getActivePinia } from "pinia";
import api from "@/services/api";
import CategoryCard from "./CategoryCard.vue";
import DestructiveConfirmDialog from "../StoreProducts/Editor/DestructiveConfirmDialog.vue";
import { useStoreCategoryRollupStore } from "@/stores/storeCategoryRollup";

// Text-like fields (rich_text) settle before persisting -- matches the
// 350ms window the dispatch brief specifies (StoreAddEdit's
// AUTOSAVE_DEBOUNCE_MS). Discrete edits (hidden toggle, banner set)
// PATCH immediately -- there's no "still typing" state to wait out.
const RICH_TEXT_DEBOUNCE_MS = 350;

// Placeholder id for a not-yet-created category row (negative so it can
// never collide with a real DB id). Cleared once the POST response
// supplies the real id.
let _nextDraftId = -1;

export default {
  name: "StoreCategories",

  components: {
    "category-card": CategoryCard,
    "destructive-confirm-dialog": DestructiveConfirmDialog,
  },

  props: {
    campaign: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
  },

  emits: ["campaign-changed"],

  data() {
    return {
      loading: true,
      hasFetchError: false,
      categories: [],
      // Ids of categories created via "Add category" this session but
      // not yet named (isNew card treatment -- Locked Decisions.md).
      // A category leaves this set the moment its name is first saved.
      unsavedCategoryIds: new Set(),
      uploadingBannerFor: null,
      _richTextTimers: {},
      // Guards against a double-POST for the same draft: a name field's
      // Enter handler blurs the input, which can fire the blur handler
      // again before the first POST resolves (e.g. Enter then Tab in
      // quick succession).
      _creatingDraftIds: new Set(),
      // Delete confirmation (C4). deleteTargetCategoryId points at the
      // ALREADY-LOADED category row in `categories` -- the dialog's
      // affected-product preview is derived from that row (see
      // deleteTargetCategory / deleteDialogConsequences below), never
      // from the DELETE response.
      deleteDialogOpen: false,
      deleteTargetCategoryId: null,
      // CC1 accordion (ADR 0059 2026-07-27 revision): the id of the ONE
      // open card, or null when every card is collapsed. Component-managed
      // UI state only -- never persisted to the backend.
      openCategoryId: null,
    };
  },

  computed: {
    // Home Page first (API already orders it first via is_home DESC), then
    // nav_order. Re-derive here defensively so the render never depends on
    // fetch-order alone.
    orderedCategories() {
      return [...this.categories].sort((a, b) => {
        if (a.is_home !== b.is_home) return a.is_home ? -1 : 1;
        return (a.nav_order ?? 0) - (b.nav_order ?? 0);
      });
    },

    // Home Page card(s) -- pinned first, rendered OUTSIDE the draggable
    // container so they can never be reordered by drag.
    homeCategories() {
      return this.orderedCategories.filter((c) => c.is_home);
    },

    // The draggable set -- everything except Home, in nav_order. The
    // index a drag reports (sortablejs old/new index) is an index INTO
    // this array; onCategoryReorder maps it back to a category row.
    nonHomeCategories() {
      return this.orderedCategories.filter((c) => !c.is_home);
    },

    // "N categories" / "1 category" -- rendered in this section's own
    // page-head count. The sidebar nav subtitle (StoreAddEdit.vue
    // sectionSubtitle) no longer reads this directly; it reads the
    // shared storeCategoryRollup Pinia store below, which this section
    // publishes into (fetchCategories / onAddCategory /
    // onConfirmDeleteCategory) so the two counts can never disagree.
    categoryCountLabel() {
      const n = this.categories.length;
      return `${n} categor${n === 1 ? "y" : "ies"}`;
    },

    // Shared category-rollup Pinia store (mirrors StoreDecorations.vue's
    // use of useDecorationRollupStore) -- this section is the
    // authoritative source for the category list, so it publishes its
    // count into the shared store on every change (fetch resolves,
    // add, delete) so the nav subtitle in StoreAddEdit.vue stays live
    // without a second network round-trip. Guarded on getActivePinia()
    // the same way StoreAddEdit.vue guards rollupStore: this component
    // mounts in unit tests without a Pinia instance.
    categoryRollupStore() {
      if (!getActivePinia()) return null;
      return useStoreCategoryRollupStore();
    },

    storeId() {
      return this.campaign?._jv?.id ?? this.campaign?.id ?? null;
    },

    // The store's OWN canonical storefront slug -- each category's path
    // nests under it: <storeBaseUrl>/shop/<storeSlug>/c/<category.slug>.
    // Slug-unify Phase 3 (#2286): read the canonical `slug` field (the
    // one the storefront read endpoints resolve on) rather than the
    // legacy `shopper_url` column, so the emitted Copy-link resolves.
    // getCampaignById already projects `slug` into the editor payload.
    storeSlug() {
      return this.campaign?.slug || "";
    },

    // Mirrors StoreShopper.vue's baseUrl computed exactly.
    storeBaseUrl() {
      return process.env.BASE_URL || window.location.origin;
    },

    // -----------------------------------------------------------------
    // Delete confirmation (C4)
    // -----------------------------------------------------------------
    // The category the dialog is about to delete -- looked up from the
    // page's OWN already-loaded `categories` state (the GET response),
    // NOT re-fetched and NOT the DELETE response. This is what lets the
    // dialog show the affected-product preview BEFORE the destructive
    // call is ever made.
    deleteTargetCategory() {
      if (this.deleteTargetCategoryId == null) return null;
      return this._findCategory(this.deleteTargetCategoryId);
    },

    deleteDialogDescription() {
      const category = this.deleteTargetCategory;
      if (!category) return "";
      const n = category.products?.length || 0;
      return (
        `This removes ${category.name || "this category"} from the store. ` +
        "This can't be undone. " +
        `The ${n} product${n === 1 ? "" : "s"} below won't be deleted -- ` +
        "they'll just be un-assigned from this category and stay in your store."
      );
    },

    // First 5 affected products (name + SKU), then a summary line for
    // the rest -- Locked Decisions.md "Delete confirmation". Rendered
    // through DestructiveConfirmDialog's `consequences` (a plain string
    // list), so each entry is pre-formatted text.
    deleteDialogConsequences() {
      const category = this.deleteTargetCategory;
      if (!category) return [];
      const products = Array.isArray(category.products) ? category.products : [];
      const shown = products.slice(0, 5).map((p) => `${p.name}${p.sku ? ` (${p.sku})` : ""}`);
      const more = products.length - shown.length;
      if (more > 0) shown.push(`and ${more} more`);
      return shown;
    },
  },

  watch: {
    storeId: {
      immediate: true,
      handler(id, oldId) {
        if (id && id !== oldId) {
          this.fetchCategories();
        }
      },
    },

    // (Re)bind category-list drag whenever the draggable set appears or
    // its size changes (fetch resolves, a category is added/deleted). The
    // reorderable container only renders under v-else (categories loaded),
    // so guard on the ref existing. Reorders themselves re-render from the
    // authoritative `categories` array, and sortablejs tracks the same
    // container element, so an in-place reorder needs no re-init.
    "nonHomeCategories.length": {
      handler() {
        this.$nextTick(() => {
          if (this.nonHomeCategories.length > 0) {
            this._initCategorySortable();
          } else {
            this._destroyCategorySortable();
          }
        });
      },
    },
  },

  beforeUnmount() {
    Object.values(this._richTextTimers).forEach((timer) => clearTimeout(timer));
    this._destroyCategorySortable();
  },

  methods: {
    async fetchCategories() {
      if (!this.storeId) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.hasFetchError = false;
      try {
        const resp = await api.get(`/api/stores/${this.storeId}/categories`);
        this.categories = Array.isArray(resp?.data) ? resp.data : [];
      } catch (e) {
        this.hasFetchError = true;
        this.categories = [];
      } finally {
        this.loading = false;
        // Publish into the shared store even on a fetch error/empty
        // result -- an explicit call here (rather than a watcher on
        // categories.length) is deliberate: a 0-to-0 no-op transition
        // (e.g. a fetch error right after navigating from another
        // 0-category store) would never fire a length-diff watcher,
        // leaving a stale count from a prior store visible in the nav.
        this._publishCategoryCount();
        // Apply any ?category=<id> deep link AFTER the rows exist. This
        // must not move into mounted(): the fetch is async, so at mount
        // `categories` is still empty and there would be nothing to
        // expand -- which is precisely the DIRECT-visit case (paste the
        // URL, refresh, follow a bookmark) rather than an exotic one.
        this._applyDeepLinkedCategory();
      }
    },

    // Open + scroll to the category named by ``?category=<id>``, the
    // query the store-editor Products table's category pill emits
    // (ProductsTableView.vue categoryLinkFor).
    //
    // Degrades silently by design: an unknown or non-numeric id leaves
    // the page in its ordinary all-collapsed state instead of throwing.
    // A pill can legitimately outlive the category it points at -- a
    // parallel session deletes the category, or the user follows an old
    // bookmark -- so a stale id is a NORMAL input, not an error worth
    // interrupting the page for.
    _applyDeepLinkedCategory() {
      const raw = this.$route && this.$route.query && this.$route.query.category;
      if (raw == null || raw === "") return;

      const targetId = Number(Array.isArray(raw) ? raw[0] : raw);
      if (!Number.isFinite(targetId)) return;

      // Only expand a category that actually loaded -- guarantees the
      // accordion never points at a row the user cannot see.
      const match = this.categories.find((c) => c && c.id === targetId);
      if (!match) return;

      this.openCategoryId = targetId;

      // Scroll after the expansion has rendered, reusing the same
      // ref-based lookup + smooth-scroll onAddCategory already uses so
      // both entry points land the card in the same place.
      this.$nextTick(() => {
        const cardRefs = this.$refs[this.cardRef(targetId)];
        const cardComponent = Array.isArray(cardRefs) ? cardRefs[0] : cardRefs;
        const el = cardComponent && cardComponent.$el;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },

    // Publishes this section's own authoritative category count into the
    // shared storeCategoryRollup Pinia store (see categoryRollupStore's
    // comment above) so StoreAddEdit.vue's nav subtitle stays live
    // without a second network round-trip. Called after every change to
    // `categories` that should be nav-visible: fetch (success or error),
    // add, and delete.
    _publishCategoryCount() {
      if (this.storeId && this.categoryRollupStore) {
        this.categoryRollupStore.setCount(this.storeId, this.categories.length);
      }
    },

    _findCategory(categoryId) {
      return this.categories.find((c) => c.id === categoryId) || null;
    },

    _replaceCategory(categoryId, patch) {
      this.categories = this.categories.map((c) =>
        c.id === categoryId ? { ...c, ...patch } : c,
      );
    },

    _raiseError(message) {
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch("raiseError", message);
      }
    },

    // -----------------------------------------------------------------
    // CC1 accordion: single-open bookkeeping
    // -----------------------------------------------------------------
    // Named ref per category id (`$refs["card-<id>"]`) so onAddCategory
    // can find the newly-created card's root element to scroll it into
    // view without a DOM query. Vue's `ref` binding on a v-for accepts a
    // function returning the ref name.
    cardRef(categoryId) {
      return `card-${categoryId}`;
    },

    // A card asks to become the open one; toggling the ALREADY-open card
    // closes it (collapses to none open) -- otherwise opening any card
    // collapses whatever else was open (single-open accordion).
    onToggleExpand(categoryId) {
      this.openCategoryId = this.openCategoryId === categoryId ? null : categoryId;
    },

    // -----------------------------------------------------------------
    // Add category
    // -----------------------------------------------------------------
    // Appends a placeholder ("New -- unsaved") card. It carries no real
    // id until the user names it (onUpdateName POSTs then). CC1 (ADR 0059
    // 2026-07-27 revision): the new card is also EXPANDED (collapsing any
    // other open card) and scrolled into view, since it now renders
    // collapsed by default like every other card.
    onAddCategory() {
      if (!this.editMode) return;
      const draftId = _nextDraftId--;
      const maxNavOrder = this.categories.reduce(
        (max, c) => (c.is_home ? max : Math.max(max, c.nav_order ?? 0)),
        0,
      );
      this.categories = [
        ...this.categories,
        {
          id: draftId,
          store_id: this.storeId,
          name: "",
          slug: "",
          rich_text: "",
          banner_image: null,
          hidden: false,
          is_home: false,
          nav_order: maxNavOrder + 1,
          products: [],
          hidden_product_count: 0,
        },
      ];
      this.unsavedCategoryIds = new Set([...this.unsavedCategoryIds, draftId]);
      this.openCategoryId = draftId;
      // Matches categoryCountLabel above (counts categories.length,
      // including not-yet-named drafts) so the nav count and this
      // section's own header count never disagree.
      this._publishCategoryCount();

      this.$nextTick(() => {
        const cardRefs = this.$refs[this.cardRef(draftId)];
        const cardComponent = Array.isArray(cardRefs) ? cardRefs[0] : cardRefs;
        const el = cardComponent?.$el;
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },

    // -----------------------------------------------------------------
    // Name (create-on-first-save for a draft, PATCH+reslug otherwise)
    // -----------------------------------------------------------------
    async onUpdateName(categoryId, name) {
      const category = this._findCategory(categoryId);
      if (!category) return;

      if (this.unsavedCategoryIds.has(categoryId)) {
        await this._createCategory(categoryId, name);
        return;
      }

      try {
        const resp = await api.patch(
          `/api/stores/${this.storeId}/categories/${categoryId}`,
          { name },
        );
        this._replaceCategory(categoryId, resp?.data || { name });
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to rename category", describeError(err));
        this._raiseError("Failed to rename category");
      }
    },

    async _createCategory(draftId, name) {
      if (this._creatingDraftIds.has(draftId)) return; // already in flight
      this._creatingDraftIds.add(draftId);
      try {
        const resp = await api.post(`/api/stores/${this.storeId}/categories`, {
          name,
        });
        const created = resp?.data;
        if (created && created.id != null) {
          // Swap the negative draft id for the real persisted row.
          this.categories = this.categories.map((c) =>
            c.id === draftId ? created : c,
          );
          const nextUnsaved = new Set(this.unsavedCategoryIds);
          nextUnsaved.delete(draftId);
          this.unsavedCategoryIds = nextUnsaved;
        }
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to create category", describeError(err));
        this._raiseError("Failed to create category");
      } finally {
        this._creatingDraftIds.delete(draftId);
      }
    },

    // -----------------------------------------------------------------
    // Rich text (debounced per-category)
    // -----------------------------------------------------------------
    onUpdateRichText(categoryId, html) {
      // Reflect the edit locally right away so the editor never fights
      // the user's own typing while the debounce window is open.
      this._replaceCategory(categoryId, { rich_text: html });

      if (this.unsavedCategoryIds.has(categoryId)) return; // nothing to PATCH yet

      if (this._richTextTimers[categoryId]) {
        clearTimeout(this._richTextTimers[categoryId]);
      }
      this._richTextTimers[categoryId] = setTimeout(() => {
        this._persistRichText(categoryId, html);
      }, RICH_TEXT_DEBOUNCE_MS);
    },

    async _persistRichText(categoryId, html) {
      try {
        const resp = await api.patch(
          `/api/stores/${this.storeId}/categories/${categoryId}`,
          { rich_text: html },
        );
        this._replaceCategory(categoryId, resp?.data || { rich_text: html });
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to save category text", describeError(err));
        this._raiseError("Failed to save category text");
      }
    },

    // -----------------------------------------------------------------
    // Visibility (immediate)
    // -----------------------------------------------------------------
    async onUpdateHidden(categoryId, hidden) {
      const category = this._findCategory(categoryId);
      if (!category || category.is_home) return;
      try {
        const resp = await api.patch(
          `/api/stores/${this.storeId}/categories/${categoryId}`,
          { hidden },
        );
        this._replaceCategory(categoryId, resp?.data || { hidden });
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to update visibility", describeError(err));
        this._raiseError("Failed to update visibility");
      }
    },

    // -----------------------------------------------------------------
    // Banner (upload -> PATCH banner_image; null -> clear)
    // -----------------------------------------------------------------
    async onUpdateBanner(categoryId, file) {
      if (file === null) {
        await this._persistBannerImage(categoryId, null);
        return;
      }
      this.uploadingBannerFor = categoryId;
      const form = new FormData();
      form.append("file", file);
      try {
        const resp = await api.post(`/upload_image`, form, {
        });
        const uuid = resp?.data?.uuid;
        if (uuid) {
          await this._persistBannerImage(categoryId, uuid);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to upload banner image", describeError(err));
        this._raiseError("Failed to upload banner image");
      } finally {
        this.uploadingBannerFor = null;
      }
    },

    async _persistBannerImage(categoryId, bannerImage) {
      try {
        const resp = await api.patch(
          `/api/stores/${this.storeId}/categories/${categoryId}`,
          { banner_image: bannerImage },
        );
        this._replaceCategory(categoryId, resp?.data || { banner_image: bannerImage });
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to save banner image", describeError(err));
        this._raiseError("Failed to save banner image");
      }
    },

    // -----------------------------------------------------------------
    // Category drag reorder (C3) -> nav_order
    // -----------------------------------------------------------------
    // oldIndex/newIndex are indices INTO the non-home draggable set
    // (sortablejs onEnd over the reorderable container, which excludes
    // Home). Home is pinned first, rendered outside that container, and
    // never carries a nav_order PATCH. We recompute nav_order for the
    // reordered non-home categories (sequential, starting at 1 so they
    // always sort AFTER Home's 0) and PATCH ONLY the rows whose nav_order
    // actually changed -- a minimal write set, not a bulk rewrite.
    // Persisting per-category (not one batch call) matches B1's shape:
    // nav_order is a field on the single-category PATCH endpoint; there
    // is no batch category-order endpoint. The local `categories` array
    // is updated first so the cards re-render in the new order
    // immediately (optimistic), then the PATCHes fire.
    async onCategoryReorder(oldIndex, newIndex) {
      if (oldIndex === newIndex) return;
      const reordered = [...this.nonHomeCategories];
      const [moved] = reordered.splice(oldIndex, 1);
      if (!moved) return;
      reordered.splice(newIndex, 0, moved);

      // New nav_order per non-home category = position + 1 (Home stays 0).
      const changed = [];
      const navOrderById = {};
      reordered.forEach((cat, i) => {
        const newNavOrder = i + 1;
        navOrderById[cat.id] = newNavOrder;
        if ((cat.nav_order ?? 0) !== newNavOrder) {
          changed.push({ id: cat.id, nav_order: newNavOrder });
        }
      });

      if (changed.length === 0) return;

      // Optimistic local reorder so the render settles on the new order.
      this.categories = this.categories.map((c) =>
        c.is_home ? c : { ...c, nav_order: navOrderById[c.id] ?? c.nav_order },
      );

      try {
        await Promise.all(
          changed.map(({ id, nav_order }) =>
            api.patch(`/api/stores/${this.storeId}/categories/${id}`, { nav_order }),
          ),
        );
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to reorder categories", describeError(err));
        this._raiseError("Failed to reorder categories");
        // Re-sync from the server so the UI reflects the true order after
        // a partial failure.
        this.fetchCategories();
      }
    },

    _destroyCategorySortable() {
      if (this._categorySortable) {
        this._categorySortable.destroy();
        this._categorySortable = null;
      }
    },

    // CC1 bug fix (Jake, live on 8087): during a category drag, inner
    // fields (name/RTE text inputs, the banner ImageUploader's own
    // dragover.prevent listener) were lighting up as if THEY were drop
    // targets. Root cause: sortablejs' DEFAULT backend is the browser's
    // native HTML5 Drag & Drop API -- a real dragover/drop DOM event fires
    // on every element the cursor passes over as the ghost moves,
    // including nested elements with their own unrelated dragover
    // listeners (ImageUploader.vue's dropzone has no way to know the drag
    // wasn't a file being dragged over IT). forceFallback: true makes
    // Sortable synthesize its own pointer-based drag instead of using
    // native HTML5 DnD, so no native dragover/drop event ever reaches
    // those nested listeners in the first place -- this is a structural
    // fix, not a CSS patch. Collapsing cards by default (CC1's main
    // change) also helps in practice (a collapsed card exposes no inner
    // fields to light up), but an EXPANDED card's inner fields must still
    // not react to a drag of a different (collapsed) card, which
    // forceFallback guarantees regardless of any card's open/closed state.
    // handle stays scoped to the drag icon only (unchanged) -- forceFallback
    // and the handle scope are two independent, complementary hardenings.
    _initCategorySortable() {
      this._destroyCategorySortable();
      const el = this.$refs.categoryList;
      if (!el) return;
      this._categorySortable = Sortable.create(el, {
        animation: 150,
        handle: "[data-testid^='ct-categories-drag-']",
        forceFallback: true,
        onEnd: ({ oldIndex, newIndex }) => {
          this.onCategoryReorder(oldIndex, newIndex);
        },
      });
    },

    // -----------------------------------------------------------------
    // Per-category product drag reorder (C3) -> sort_order
    // -----------------------------------------------------------------
    // CategoryCard emits the new campaign_product_id order after a drag.
    // We PATCH the product-order endpoint with EXACTLY
    // {"product_order": [{"campaign_product_id", "sort_order"}, ...]}
    // (store_categories.py::patch_category_product_order).
    //
    // On a NAMED category it writes sort_order on existing memberships
    // only -- membership creation is the Product editor's job, enforced
    // server-side. On HOME it may ALSO materialise a membership row for
    // a product that is uncategorised in this store (Jake, 2026-08-26),
    // which is what lets an adopted product hold a position at all.
    // Either way the client sends the same payload; the server decides.
    //
    // On a 400 the whole request is rejected atomically -- no partial
    // reorder and no partial materialisation -- so we surface the error
    // and re-sync from the server rather than leave a half-applied
    // local order.
    //
    // Re-rendering from the PATCH RESPONSE (not the local array) is what
    // makes the flags settle: a just-materialised product comes back
    // adopted:false with a real sort_order, so its "Uncategorised" pill
    // clears without a second fetch.
    async onUpdateProductOrder(categoryId, orderedProductIds) {
      const category = this._findCategory(categoryId);
      if (!category) return;
      const product_order = orderedProductIds.map((cpId, index) => ({
        campaign_product_id: cpId,
        sort_order: index,
      }));
      try {
        const resp = await api.patch(
          `/api/stores/${this.storeId}/categories/${categoryId}/product-order`,
          { product_order },
        );
        if (resp?.data) {
          this._replaceCategory(categoryId, resp.data);
        }
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to reorder products", describeError(err));
        this._raiseError("Failed to reorder products");
        this.fetchCategories();
      }
    },

    // -----------------------------------------------------------------
    // Delete (C4): confirm dialog first, DELETE only on confirm.
    // -----------------------------------------------------------------
    // Opens the dialog -- makes NO API call. deleteTargetCategory /
    // deleteDialogConsequences (computed, above) read the category
    // straight out of `categories`, so the affected-product preview is
    // available immediately, before any DELETE is ever issued.
    onDeleteCategory(categoryId) {
      const category = this._findCategory(categoryId);
      if (!category || category.is_home) return; // Home has no delete affordance
      this.deleteTargetCategoryId = categoryId;
      this.deleteDialogOpen = true;
    },

    onCancelDeleteCategory() {
      this.deleteDialogOpen = false;
      this.deleteTargetCategoryId = null;
    },

    async onConfirmDeleteCategory() {
      const categoryId = this.deleteTargetCategoryId;
      if (categoryId == null) return;

      // An unsaved draft (negative placeholder id, never POSTed to B1 --
      // see onAddCategory) has nothing on the server to delete. Drop it
      // locally; no DELETE call, no campaign-changed (nothing persisted
      // ever existed).
      if (this.unsavedCategoryIds.has(categoryId)) {
        this.categories = this.categories.filter((c) => c.id !== categoryId);
        const nextUnsaved = new Set(this.unsavedCategoryIds);
        nextUnsaved.delete(categoryId);
        this.unsavedCategoryIds = nextUnsaved;
        if (this.openCategoryId === categoryId) this.openCategoryId = null;
        this.deleteDialogOpen = false;
        this.deleteTargetCategoryId = null;
        this._publishCategoryCount();
        return;
      }

      try {
        await api.delete(`/api/stores/${this.storeId}/categories/${categoryId}`);
        this.categories = this.categories.filter((c) => c.id !== categoryId);
        if (this.openCategoryId === categoryId) this.openCategoryId = null;
        this._publishCategoryCount();
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to delete category", describeError(err));
        this._raiseError("Failed to delete category");
      } finally {
        this.deleteDialogOpen = false;
        this.deleteTargetCategoryId = null;
      }
    },

    // C1 makes no writes -- always valid so the parent's validate sweep
    // never blocks on this section. C2 still has no cross-field
    // validation to enforce beyond what each PATCH already validates
    // server-side, so this stays a no-op.
    async validateTab() {
      return true;
    },

    // C1/C2 persist directly to B1 as each field is edited (see class
    // doc comment) rather than through the campaign object -- so this
    // stays a no-op that resolves the campaign unchanged, same
    // contract as C1.
    async updateCampaign(campaign) {
      return campaign;
    },
  },
};
</script>

<style scoped>
.store-categories__loading {
  color: var(--tf-fg-4, #616161);
  padding: 12px 0;
}

.store-categories__error,
.store-categories__empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 24px;
  border: 1px dashed var(--tf-border, #d1d5db);
  border-radius: var(--tf-radius-md, 12px);
}

.store-categories__error-icon,
.store-categories__empty-icon {
  font-size: 28px;
  color: var(--tf-primary, #119ae9);
}

.store-categories__error-title,
.store-categories__empty-title {
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: var(--tf-secondary, #133752);
  margin: 0;
}

.store-categories__error-body,
.store-categories__empty-body {
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: var(--tf-fg-4, #616161);
  margin: 0;
}

.store-categories__retry {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid var(--tf-border, #d1d5db);
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  color: var(--tf-secondary, #133752);
}

.store-categories__page-head {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 22px;
}

.store-categories__page-head-copy {
  flex: 1;
}

.store-categories__page-title {
  font-family: "Poppins", sans-serif;
  font-size: 28px;
  font-weight: 500;
  color: var(--tf-secondary, #133752);
  letter-spacing: -0.3px;
  margin: 0 0 8px;
}

.store-categories__page-lede {
  font-family: "Roboto", sans-serif;
  font-size: 14.5px;
  color: var(--tf-fg-3, #616161);
  line-height: 1.55;
  margin: 0;
  max-width: 760px;
}

.store-categories__count {
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  color: var(--tf-fg-4, #757575);
  white-space: nowrap;
  padding-bottom: 4px;
}

.store-categories__list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* The non-home cards live in their own flex column so sortablejs
   reorders them without disturbing the pinned Home card or the add bar.
   Same 16px rhythm as the parent list. */
.store-categories__reorderable {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* CC1 (ADR 0059 2026-07-27 revision): now sits ABOVE the category list
   (was the last item inside .store-categories__list's flex column,
   which supplied its spacing via `gap`) -- margin-bottom replaces that
   gap now that it's a standalone sibling. */
.store-categories__add-bar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  margin-bottom: 22px;
  border: 1.5px dashed var(--tf-border-strong, #d6d6d6);
  border-radius: 12px;
  background: #fff;
  color: var(--tf-primary, #119ae9);
  font-size: 14.5px;
  font-weight: 500;
  cursor: pointer;
  font-family: "Roboto", sans-serif;
}

.store-categories__add-bar:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* ADR 0022 empty-state convention: concrete + actionable, not a bare
   "no data" message. Shown when only Home Page exists yet. */
.store-categories__empty-state {
  border: 1px dashed var(--tf-border-strong, #d6d6d6);
  border-radius: 12px;
  padding: 26px 22px;
  text-align: center;
  background: #fff;
}

.store-categories__empty-state-title {
  font-family: "Poppins", sans-serif;
  font-size: 15.5px;
  font-weight: 500;
  color: var(--tf-fg-1, #212121);
  margin: 0 0 4px;
}

.store-categories__empty-state-body {
  font-family: "Roboto", sans-serif;
  font-size: 13.5px;
  color: var(--tf-fg-3, #616161);
  line-height: 1.55;
  max-width: 460px;
  margin: 0 auto 16px;
}

.store-categories__empty-state-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 9999px;
  background: var(--tf-primary, #119ae9);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: "Roboto", sans-serif;
}

.store-categories__empty-state-cta:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
